import { useMemo } from 'react';
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import SearchSelect, { type SearchSelectOption } from '@/components/Shared/SearchSelect';
import { CategoryBadge } from '@/pages/Import/components/CategoryBadge';
import {
  ScheduleRuleType,
  ScheduleRuleTypeLabels,
  WeekdayLabels,
  WeekdayOptions,
  MonthLabels,
  daysInMonth,
  formatScheduleRule,
} from '@/utils/scheduleRuleConstants';
import { newRuleSpec, specToFormattable } from '@/pages/Import/Contract/logic/mapExtraction';
import { type PositionDecision, type RuleSpec } from '@/pages/Import/Contract/logic/types';
import { BillingRuleFields } from '@/pages/Offers/components/BillingRuleFields';
import { formatMoney } from '@/utils/money';
import { isBillingRuleComplete, needsReview } from '@/utils/billingKind';
import { useContractImportStore } from '@/store/useContractImportStore';
import { ContractStepFooter } from './ContractStepFooter';
import { Field, INPUT, Kicker, ModeToggle } from './wizardUi';

/** `ServiceCategory` enum names (BE Services module). */
const SERVICE_CATEGORIES = ['Other', 'Cleaning', 'Sanitary', 'Stairwell', 'GreenSpace'] as const;

export function PositionsStep() {
  const { t } = useTranslation('contractImport');
  const { extraction, positions, setPosition, live } = useContractImportStore();

  const serviceOptions: SearchSelectOption[] = useMemo(
    () =>
      live.services.map((s) => ({
        id: s.id,
        label: s.name,
        description: s.category ? t(`positions.categories.${s.category}`, { defaultValue: s.category }) : undefined,
      })),
    [live.services, t],
  );

  const flatRates = extraction?.combinedFlatRates ?? [];
  const includedCount = positions.filter((p) => p.include).length;

  return (
    <div className="space-y-4">
      {flatRates.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12px] text-sky-800">
          <Info className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
          <div className="space-y-1">
            <div className="font-medium">{t('positions.flatRate.title')}</div>
            {flatRates.map((flat, i) => (
              <div key={i} className="tabular-nums">
                {t('positions.flatRate.entry', {
                  amount: formatMoney(flat.netAmount),
                  positions: (flat.appliesToPositionNumbers ?? []).join(', '),
                })}
              </div>
            ))}
            <div>{t('positions.flatRate.allocationNote')}</div>
          </div>
        </div>
      )}

      {positions.map((position) => (
        <PositionCard
          key={position.key}
          position={position}
          serviceOptions={serviceOptions}
          onChange={(patch) => setPosition(position.key, patch)}
        />
      ))}

      {positions.length === 0 && (
        <Card>
          <p className="px-3 py-6 text-center text-[12px] text-slate-500">{t('positions.empty')}</p>
        </Card>
      )}

      <ContractStepFooter
        back="object"
        next="summary"
        summary={t('positions.summary', { count: includedCount, total: positions.length })}
      />
    </div>
  );
}

function PositionCard({
  position,
  serviceOptions,
  onChange,
}: {
  position: PositionDecision;
  serviceOptions: SearchSelectOption[];
  onChange: (patch: Partial<PositionDecision>) => void;
}) {
  const { t } = useTranslation('contractImport');

  const suggested: SearchSelectOption[] = position.serviceMatch.suggestion
    ? [{ id: position.serviceMatch.suggestion.id, label: position.serviceMatch.suggestion.name }]
    : [];

  const setRule = (id: string, patch: Partial<RuleSpec>) =>
    onChange({
      scheduleRules: position.scheduleRules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });

  const setChecklistItem = (index: number, patch: Partial<PositionDecision['checklistItems'][number]>) =>
    onChange({
      checklistItems: position.checklistItems.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });

  const moveChecklistItem = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= position.checklistItems.length) return;
    const items = [...position.checklistItems];
    [items[index], items[target]] = [items[target], items[index]];
    onChange({ checklistItems: items });
  };

  return (
    <Card className={position.include ? '' : 'opacity-60'}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400 tabular-nums whitespace-nowrap">
                {t('positions.positionNumber', { number: position.positionNumber })}
              </span>
              {/* The heading is the offer title — the text that ends up on the offer document. */}
              <input
                className="flex-1 min-w-0 rounded-md border border-transparent px-2 py-1 -ml-2 text-[15px] font-semibold tracking-tight text-slate-900 placeholder:font-normal placeholder:text-slate-400 hover:border-slate-200 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
                value={position.offerTitle}
                maxLength={200}
                placeholder={t('positions.price.offerTitle')}
                aria-label={t('positions.price.offerTitle')}
                onChange={(e) => onChange({ offerTitle: e.target.value })}
              />
            </div>
            {position.include && !position.offerTitle.trim() && (
              <p className="text-[11px] text-amber-600">{t('positions.price.offerTitleRequired')}</p>
            )}
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={position.include}
              onChange={(e) => onChange({ include: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
            />
            {t('positions.include')}
          </label>
        </div>

        {!position.include ? (
          <p className="text-[12px] text-slate-400">{t('positions.excluded')}</p>
        ) : (
          <div className="space-y-4">
            {/* Service */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="flex items-center gap-3">
                <Kicker>{t('positions.service.title')}</Kicker>
                <CategoryBadge category={position.serviceMatch.category} score={position.serviceMatch.score} />
              </div>
              <ModeToggle
                mode={position.service.mode}
                onChange={(mode) => onChange({ service: { ...position.service, mode } })}
              />
              {position.service.mode === 'link' ? (
                <SearchSelect
                  options={serviceOptions}
                  suggestedOptions={suggested}
                  value={position.service.existingServiceId}
                  onChange={(id) => onChange({ service: { ...position.service, existingServiceId: id } })}
                  placeholder={t('positions.service.pickService')}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                  <Field label={t('positions.service.name')}>
                    <input
                      className={INPUT}
                      value={position.service.name}
                      onChange={(e) => onChange({ service: { ...position.service, name: e.target.value } })}
                    />
                  </Field>
                  <Field label={t('positions.service.category')}>
                    <select
                      className={INPUT}
                      value={position.service.category}
                      onChange={(e) => onChange({ service: { ...position.service, category: e.target.value } })}
                    >
                      {SERVICE_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{t(`positions.categories.${c}`)}</option>
                      ))}
                    </select>
                  </Field>
                </div>
              )}
            </div>

            {/* Task */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <Kicker>{t('positions.task.title')}</Kicker>
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                <Field label={t('positions.task.name')}>
                  <input className={INPUT} value={position.taskName} onChange={(e) => onChange({ taskName: e.target.value })} />
                </Field>
                <Field label={t('positions.task.duration')}>
                  <input
                    className={`${INPUT} tabular-nums`}
                    type="number"
                    min={1}
                    max={1440}
                    value={position.durationMinutes}
                    onChange={(e) => onChange({ durationMinutes: parseInt(e.target.value) || 0 })}
                  />
                </Field>
              </div>
            </div>

            {/* Schedule rules */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Kicker>{t('positions.rules.title')}</Kicker>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
                  onClick={() =>
                    onChange({ scheduleRules: [...position.scheduleRules, newRuleSpec(ScheduleRuleType.Weekly)] })
                  }
                >
                  {t('actions.addRule')}
                </Button>
              </div>

              {position.scheduleRules.length === 0 && (
                <p className="text-[12px] text-slate-400">{t('positions.rules.none')}</p>
              )}

              {position.scheduleRules.map((rule) => (
                <RuleEditor
                  key={rule.id}
                  rule={rule}
                  onChange={(patch) => setRule(rule.id, patch)}
                  onRemove={() =>
                    onChange({ scheduleRules: position.scheduleRules.filter((r) => r.id !== rule.id) })
                  }
                />
              ))}
            </div>

            {/* Checklist */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Kicker>{t('positions.checklist.title')}</Kicker>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
                  onClick={() =>
                    onChange({ checklistItems: [...position.checklistItems, { title: '', intervalHint: '' }] })
                  }
                >
                  {t('actions.addChecklistItem')}
                </Button>
              </div>

              {position.checklistItems.length === 0 && (
                <p className="text-[12px] text-slate-400">{t('positions.checklist.empty')}</p>
              )}

              {position.checklistItems.map((item, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] gap-2 items-center">
                  <input
                    className={INPUT}
                    value={item.title}
                    onChange={(e) => setChecklistItem(index, { title: e.target.value })}
                    placeholder={t('positions.checklist.itemTitle')}
                    aria-label={t('positions.checklist.itemTitle')}
                  />
                  <input
                    className={INPUT}
                    value={item.intervalHint}
                    onChange={(e) => setChecklistItem(index, { intervalHint: e.target.value })}
                    placeholder={t('positions.checklist.intervalHint')}
                    aria-label={t('positions.checklist.intervalHint')}
                  />
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1"
                      title={t('actions.moveUp')}
                      disabled={index === 0}
                      onClick={() => moveChecklistItem(index, -1)}
                    >
                      <ArrowUp className="w-3.5 h-3.5" strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1"
                      title={t('actions.moveDown')}
                      disabled={index === position.checklistItems.length - 1}
                      onClick={() => moveChecklistItem(index, 1)}
                    >
                      <ArrowDown className="w-3.5 h-3.5" strokeWidth={2} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1"
                      title={t('actions.remove')}
                      onClick={() =>
                        onChange({ checklistItems: position.checklistItems.filter((_, i) => i !== index) })
                      }
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-600" strokeWidth={2} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Price */}
            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <Kicker>{t('positions.price.title')}</Kicker>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr] gap-3 items-end">
                <Field label={t('positions.price.netAmount')}>
                  <input
                    className={`${INPUT} tabular-nums`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={position.netAmount}
                    onChange={(e) => onChange({ netAmount: parseFloat(e.target.value) || 0 })}
                  />
                </Field>
                <Field label={t('positions.price.intervalText')}>
                  <input
                    className={INPUT}
                    value={position.intervalText}
                    onChange={(e) => onChange({ intervalText: e.target.value })}
                  />
                </Field>
              </div>
              {!position.intervalText.trim() && (
                <p className="text-[11px] text-amber-600">{t('positions.price.intervalTextRequired')}</p>
              )}
              {/* why: the same editor as on the offer, so season, month and advance/arrears can be set
                  here and the price lands in the rhythm it is meant for (FAFD-27). */}
              <BillingRuleFields
                kind={position.recurrenceKind}
                rule={position.billingRule}
                onChange={(recurrenceKind, billingRule) => onChange({ recurrenceKind, billingRule })}
                allowUnknown
                error={
                  !needsReview(position.recurrenceKind)
                  && !isBillingRuleComplete(position.recurrenceKind, position.billingRule)
                    ? t('summary.issues.billingRule', { number: position.positionNumber })
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function RuleEditor({
  rule,
  onChange,
  onRemove,
}: {
  rule: RuleSpec;
  onChange: (patch: Partial<RuleSpec>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('contractImport');
  const needsWindow = rule.type === ScheduleRuleType.BiWeekly || rule.type === ScheduleRuleType.Seasonal;

  const monthDaySelect = (
    month: number,
    day: number,
    onMonth: (m: number) => void,
    onDay: (d: number) => void,
    label: string,
  ) => (
    <Field label={label}>
      <div className="grid grid-cols-2 gap-1.5">
        <select className={INPUT} value={day} onChange={(e) => onDay(parseInt(e.target.value))} aria-label={label}>
          {Array.from({ length: daysInMonth(month) }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          className={INPUT}
          value={month}
          onChange={(e) => {
            const m = parseInt(e.target.value);
            onMonth(m);
            if (day > daysInMonth(m)) onDay(daysInMonth(m));
          }}
          aria-label={label}
        >
          {Object.entries(MonthLabels).map(([value, name]) => (
            <option key={value} value={value}>{name}</option>
          ))}
        </select>
      </div>
    </Field>
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 min-w-0">
          <Field label={t('positions.rules.type')}>
            <select
              className={INPUT}
              value={rule.type}
              onChange={(e) => onChange({ type: parseInt(e.target.value) })}
            >
              {Object.entries(ScheduleRuleTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>

          {rule.type === ScheduleRuleType.Weekly && (
            <Field label={t('positions.rules.weekdays')}>
              <div className="flex flex-wrap gap-1">
                {WeekdayOptions.map((day) => {
                  const selected = rule.weekdays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={selected}
                      title={WeekdayLabels[day]}
                      onClick={() =>
                        onChange({
                          weekdays: selected
                            ? rule.weekdays.filter((d) => d !== day)
                            : [...rule.weekdays, day],
                        })
                      }
                      className={`px-2 py-1 rounded-md text-[12px] font-medium transition-colors ${
                        selected ? 'bg-[#00A871] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {WeekdayLabels[day].substring(0, 2)}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
        </div>

        <Button variant="ghost" size="sm" className="p-1 mt-5" title={t('actions.removeRule')} onClick={onRemove}>
          <Trash2 className="w-3.5 h-3.5 text-red-600" strokeWidth={2} />
        </Button>
      </div>

      {needsWindow && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {monthDaySelect(
            rule.windowFromMonth, rule.windowFromDay,
            (m) => onChange({ windowFromMonth: m }),
            (d) => onChange({ windowFromDay: d }),
            t('positions.rules.windowFrom'),
          )}
          {monthDaySelect(
            rule.windowToMonth, rule.windowToDay,
            (m) => onChange({ windowToMonth: m }),
            (d) => onChange({ windowToDay: d }),
            t('positions.rules.windowTo'),
          )}
        </div>
      )}

      <div className="text-[12px] text-slate-500">
        {t('positions.rules.preview')}: <span className="text-slate-700">{formatScheduleRule(specToFormattable(rule))}</span>
      </div>
    </div>
  );
}

export default PositionsStep;
